<template>
  <div class="sip-portfolio-tab">
    <!-- Initial Loading State -->
    <div v-if="isInitialLoading" class="initial-loading-state">
      <div class="initial-loading-content">
        <div class="loading-spinner-container">
          <i class="pi pi-spin pi-spinner loading-spinner"></i>
        </div>
        <h3>Preparing Your Portfolio View</h3>
        <p>We're customizing your experience and loading your SIP allocations...</p>
        <div class="loading-dots">
          <span></span>
          <span></span>
          <span></span>
        </div>
      </div>
    </div>

    <!-- Tier 3 Workflow: Questionnaire, Review, Recommendation -->
    <div v-else-if="showMFRecommender" class="mf-recommender-container">
      <div class="workflow-header">
        <div class="workflow-steps">
          <span :class="['workflow-step', { active: tier3Step === 1 }]">1. Questionnaire</span>
          <span :class="['workflow-step', { active: tier3Step === 2 }]">2. Review</span>
          <span :class="['workflow-step', { active: tier3Step === 3 }]">3. Recommendation</span>
        </div>
        <button class="back-link-btn" @click="backToTierSelection">
          ← Back to Tier Selection
        </button>
      </div>

      <SIPQuestionnaire
        v-if="tier3Step === 1"
        :initialAnswers="tier3Answers"
        :disabled="isLoading"
        @complete="handleQuestionnaireComplete"
      />

      <SIPReview
        v-else-if="tier3Step === 2"
        :answers="tier3Answers"
        :disabled="isLoading"
        @edit="handleReviewEdit"
        @submit="handleReviewSubmit"
      />

      <SIPRecommendation
        v-else-if="tier3Step === 3"
        :recommendations="tier3Recommendations"
        :sipAmount="selectedSIPAmount"
        :loading="isLoading"
        @save="handleSaveRecommendation"
      />
    </div>    <!-- Range Selection Screen -->
    <div v-else-if="!hasSIPAllocation" class="range-selection-container">
      <div class="intro-section">
        <h2>Choose Your SIP Investment Range</h2>
        <p class="intro-text">
          Select your monthly SIP investment capacity to get personalized mutual fund recommendations tailored to your budget.
        </p>
      </div>

      <div class="range-cards-grid">
        <div
          v-for="range in sipRanges"
          :key="range.id"
          class="range-card"
          :class="{ 'selected': selectedRange === range.id }"
          @click="selectRange(range.id)"
        >
          <div class="range-header">
            <i class="pi pi-wallet range-icon"></i>
            <h3>{{ range.label }}</h3>
          </div>
          <div class="range-amount">
            ₹{{ formatNumber(range.min) }} - ₹{{ formatNumber(range.max) }}
          </div>
          <div class="range-description">
            {{ range.description }}
          </div>
          <div class="range-badge" v-if="range.id === 3">
            <i class="pi pi-star-fill"></i>
            Personalized
          </div>
        </div>
      </div>

      <div class="action-section" v-if="selectedRange">
        <div class="sip-amount-input-section">
          <label class="input-label">Enter Your Monthly SIP Amount</label>
          <div class="input-group">
            <span class="currency-symbol">₹</span>
            <InputNumber
              v-model="selectedSIPAmount"
              :min="sipRanges.find(r => r.id === selectedRange).min"
              :max="sipRanges.find(r => r.id === selectedRange).max"
              :step="100"
              placeholder="Enter amount"
              class="sip-amount-input"
            />
          </div>
          <p class="input-hint">
            Range: ₹{{ formatNumber(sipRanges.find(r => r.id === selectedRange).min) }} -
            ₹{{ formatNumber(sipRanges.find(r => r.id === selectedRange).max) }}
          </p>
        </div>
        <ANButton
          label="Get Allocation"
          icon="pi pi-arrow-right"
          variant="primary"
          :disabled="!selectedSIPAmount || selectedSIPAmount < sipRanges.find(r => r.id === selectedRange).min"
          @click="handleAllocate"
          class="allocate-btn"
        />
      </div>
    </div>

    <!-- SIP Allocation Results -->
    <div v-else-if="sipAllocationData" class="sip-allocation-results">
      <div class="allocation-header">
        <h2>Your SIP-Based Portfolio</h2>
        <p class="allocation-subtitle">Monthly SIP: ₹{{ formatNumber(sipAllocationData.monthlyAmount) }}</p>
        <div class="tier-info" v-if="sipAllocationData.tierRange">
          <span class="tier-badge">{{ getTierLabel(sipAllocationData.sipTier) }}</span>
          <span class="tier-range">₹{{ sipAllocationData.tierRange }}</span>
        </div>
      </div>

      <!-- Mutual Fund Allocation Cards -->
      <div class="fund-cards-grid">
        <div
          v-for="fund in sipAllocationData.funds"
          :key="fund.fundName"
          class="fund-card"
        >
          <div class="fund-header">
            <h3>{{ fund.fundName }}</h3>
            <span class="fund-category-badge">{{ fund.category }}</span>
          </div>
          <div class="fund-allocation-amount">
            ₹{{ formatNumber(fund.sipAmount) }}/month
          </div>
          <div class="fund-percentage">
            {{ fund.percentage }}% of total SIP
          </div>
          <div class="fund-details">
            <div class="fund-detail-item">
              <span class="detail-label">Risk:</span>
              <span class="detail-value" :class="`risk-${fund.risk.toLowerCase()}`">
                {{ fund.risk }}
              </span>
            </div>
            <div class="fund-detail-item" v-if="fund.expectedReturn">
              <span class="detail-label">Expected Return:</span>
              <span class="detail-value">{{ fund.expectedReturn }}% p.a.</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Questionnaire Answers Accordion (Only for Tier 3) -->
      <div v-if="sipAllocationData.questionnaireAnswers && Object.keys(sipAllocationData.questionnaireAnswers).length > 0 && selectedRange === 3" class="questionnaire-section">
        <div class="questionnaire-card">
          <Accordion>
            <AccordionPanel value="preferences">
              <AccordionHeader>
                <span class="accordion-header-content">
                  <i class="pi pi-list-check"></i>
                  View Your Investment Preferences
                </span>
              </AccordionHeader>
              <AccordionContent>
                <div class="preferences-content">
                  <p class="preferences-intro">
                    <i class="pi pi-info-circle"></i>
                    Your personalized SIP portfolio was tailored based on these preferences:
                  </p>
                  <div class="preferences-grid">
                    <div
                      v-for="(answer, question) in sipAllocationData.questionnaireAnswers"
                      :key="question"
                      class="preference-card"
                    >
                      <div class="preference-label">{{ formatQuestionKey(question) }}</div>
                      <div class="preference-value">{{ formatAnswerValue(answer) }}</div>
                    </div>
                  </div>
                </div>
              </AccordionContent>
            </AccordionPanel>
          </Accordion>
        </div>
      </div>

      <!-- Action Buttons -->
      <div class="action-buttons">
        <ANButton
          label="Change SIP Tier"
          icon="pi pi-refresh"
          variant="secondary"
          @click="changeTier"
          class="action-btn"
        />
        <ANButton
          label="Go to Dashboard"
          variant="primary"
          @click="goToDashboard"
          class="action-btn"
        />
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="isLoading" class="loading-state">
      <div class="loading-content">
        <i class="pi pi-spinner pi-spin loading-icon"></i>
        <h3>Creating Your SIP Portfolio...</h3>
        <p>Please wait while we generate your personalized mutual fund recommendations.</p>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useUserStore } from '@/store/user-store'
import ANButton from '@/components/common/AnButton.vue'
import SIPQuestionnaire from '@/components/portfolio/SIPQuestionnaire.vue'
import SIPReview from '@/components/portfolio/SIPReview.vue'
import SIPRecommendation from '@/components/portfolio/SIPRecommendation.vue'
import Accordion from 'primevue/accordion'
import AccordionPanel from 'primevue/accordionpanel'
import AccordionHeader from 'primevue/accordionheader'
import AccordionContent from 'primevue/accordioncontent'
import InputNumber from 'primevue/inputnumber'

const store = useUserStore()

// SIP Ranges
const sipRanges = [
  {
    id: 1,
    label: 'Starter',
    min: 500,
    max: 5000,
    description: 'Perfect for beginners starting their investment journey'
  },
  {
    id: 2,
    label: 'Growth',
    min: 5000,
    max: 15000,
    description: 'Build a diversified portfolio with moderate investments'
  },
  {
    id: 3,
    label: 'Advanced',
    min: 15000,
    max: 50000,
    description: 'Customized allocation based on your preferences and goals'
  }
]

// State
const selectedRange = ref(null)
const selectedSIPAmount = ref(null)
const showMFRecommender = ref(false)
const sipAllocationData = ref(null)
const isLoading = ref(false)
const isInitialLoading = ref(true) // Loading state for initial data fetch

// Tier 3 specific state
const tier3Step = ref(1) // 1=questionnaire, 2=review, 3=recommendation
const tier3Answers = ref({})
const tier3Recommendations = ref(null)

// Computed
const hasSIPAllocation = computed(() => {
  // Only show allocation results if we have data AND we're not showing the recommender
  return sipAllocationData.value !== null && !showMFRecommender.value
})

// Methods
const selectRange = (rangeId) => {
  selectedRange.value = rangeId
}

const handleAllocate = async () => {
  if (!selectedRange.value || !selectedSIPAmount.value) return

  const range = sipRanges.find(r => r.id === selectedRange.value)
  const today = new Date().toISOString().split('T')[0] // Format: YYYY-MM-DD

  // Validate amount is within range
  if (selectedSIPAmount.value < range.min || selectedSIPAmount.value > range.max) {
    store.fireAlert('Error', `Please enter an amount between ₹${formatNumber(range.min)} and ₹${formatNumber(range.max)}`)
    return
  }

  if (selectedRange.value === 3) {
    // For premium range (Tier 3), show questionnaire flow
    console.log('=== STARTING TIER 3 FLOW ===')
    console.log('Selected SIP amount:', selectedSIPAmount.value)
    showMFRecommender.value = true
    tier3Step.value = 1
    tier3Answers.value = {}
    tier3Recommendations.value = null
  } else {
    // For ranges 1 and 2 (Tier 1 & 2), use static allocation with user's selected amount
    isLoading.value = true
    try {
      const allocation = await getStaticAllocation(selectedRange.value, selectedSIPAmount.value)
      sipAllocationData.value = allocation

      // Save to database with correct format
      await store.saveSIPAllocation({
        customerId: store.dashboardData?.customerId,
        sipTier: selectedRange.value,
        tierRange: `${range.min}-${range.max}`,
        allocations: allocation.funds.map(fund => ({
          fundName: fund.fundName,
          assetClass: fund.category,
          allocationPercentage: fund.percentage,
          monthlySIP: fund.sipAmount
        })),
        questionnaireAnswers: null, // Not applicable for Tier 1 & 2
        createdAt: today,
        lastModified: today
      })

      store.fireAlert('Success', 'SIP allocation created successfully!')
    } catch (error) {
      console.error('Failed to create SIP allocation:', error)
      store.fireAlert('Error', 'Failed to create SIP allocation. Please try again.')
    } finally {
      isLoading.value = false
    }
  }
}

const getStaticAllocation = async (rangeId, amount) => {
  // Static allocations for ranges 1 and 2
  const allocations = {
    1: {
      monthlyAmount: amount,
      funds: [
        {
          fundName: 'HDFC Index Fund - Nifty 50',
          category: 'Large Cap - Index',
          sipAmount: amount * 0.6,
          percentage: 60,
          risk: 'Low',
          expectedReturn: 12
        },
        {
          fundName: 'HDFC Balanced Advantage Fund',
          category: 'Hybrid',
          sipAmount: amount * 0.4,
          percentage: 40,
          risk: 'Low',
          expectedReturn: 10
        }
      ]
    },
    2: {
      monthlyAmount: amount,
      funds: [
        {
          fundName: 'Parag Parikh Flexi Cap Fund',
          category: 'Flexi Cap',
          sipAmount: amount * 0.4,
          percentage: 40,
          risk: 'Medium',
          expectedReturn: 14
        },
        {
          fundName: 'Axis Mid Cap Fund',
          category: 'Mid Cap',
          sipAmount: amount * 0.3,
          percentage: 30,
          risk: 'Medium',
          expectedReturn: 15
        },
        {
          fundName: 'ICICI Prudential Equity & Debt Fund',
          category: 'Hybrid',
          sipAmount: amount * 0.3,
          percentage: 30,
          risk: 'Low',
          expectedReturn: 11
        }
      ]
    }
  }

  return allocations[rangeId]
}

const handleAllocationComplete = async (allocationData) => {
  console.log('=== handleAllocationComplete START ===')
  console.log('Raw allocationData:', JSON.stringify(allocationData, null, 2))
  console.log('questionnaireAnswers from input:', JSON.stringify(allocationData.questionnaireAnswers, null, 2))

  const today = new Date().toISOString().split('T')[0]

  // Combine anchors and satellites into a single funds array
  const anchors = allocationData.anchors || []
  const satellites = allocationData.satellites || []
  const allFunds = [...anchors, ...satellites]

  console.log('Anchors count:', anchors.length)
  console.log('Satellites count:', satellites.length)
  console.log('Total funds:', allFunds.length)

  // Calculate total allocation to normalize if needed
  let totalAllocation = allFunds.reduce((sum, fund) => sum + (fund.allocation || 0), 0)
  console.log('Total allocation from API:', totalAllocation)

  // Transform to SIPPortfolioTab format
  const transformedData = {
    monthlyAmount: selectedSIPAmount.value,
    funds: allFunds.map((fund, index) => {
      console.log(`Processing fund ${index + 1}:`, fund)

      // Get percentage - API returns as allocationPercent (e.g., 35, 21, 14.67)
      const percentage = fund.allocationPercent || fund.allocation || 0
      const sipAmount = (selectedSIPAmount.value * percentage) / 100

      console.log(`  - Fund: ${fund.fundName}`)
      console.log(`  - Allocation %: ${percentage}`)
      console.log(`  - SIP Amount: ₹${Math.round(sipAmount)}`)

      return {
        fundName: fund.fundName || fund.name || `Fund ${index + 1}`,
        category: fund.category || fund.assetClass || 'Unknown',
        sipAmount: Math.round(sipAmount), // Round to nearest rupee
        percentage: percentage,
        risk: fund.risk || fund.riskLevel || 'Medium',
        expectedReturn: fund.returns?.threeY || fund.expectedReturn || null
      }
    }),
    questionnaireAnswers: allocationData.questionnaireAnswers || null,
    sipTier: 3,
    tierRange: '15000-50000'
  }

  console.log('Transformed data:', JSON.stringify(transformedData, null, 2))
  console.log('Transformed questionnaireAnswers:', JSON.stringify(transformedData.questionnaireAnswers, null, 2))
  console.log('Number of funds:', transformedData.funds.length)

  sipAllocationData.value = transformedData
  showMFRecommender.value = false

  // Save to database
  try {
    const savePayload = {
      customerId: store.dashboardData?.customerId,
      sipTier: '3',
      tierRange: '15000-50000',
      allocations: transformedData.funds.map(fund => ({
        fundName: fund.fundName,
        assetClass: fund.category,
        allocationPercentage: fund.percentage,
        monthlySIP: fund.sipAmount
      })),
      questionnaireAnswers: transformedData.questionnaireAnswers,
      createdAt: today,
      lastModified: today
    }

    console.log('=== SAVING TO DB ===')
    console.log('Save payload:', JSON.stringify(savePayload, null, 2))

    await store.saveSIPAllocation(savePayload)
    console.log('=== SAVED SUCCESSFULLY ===')

    store.fireAlert('Success', 'SIP allocation saved successfully!')
  } catch (error) {
    console.error('=== SAVE FAILED ===', error)
    store.fireAlert('Error', 'Failed to save allocation. Please try again.')
  }
}

const changeTier = () => {
  // Reset to show tier selection screen
  selectedRange.value = null
  selectedSIPAmount.value = null
  showMFRecommender.value = false
  sipAllocationData.value = null

  // Clear from dashboard data to trigger tier selection view
  if (store.dashboardData && store.dashboardData.sipPortfolioAllocation) {
    store.dashboardData.sipPortfolioAllocation = null
  }

  console.log('Tier change initiated - showing selection screen')
}

const backToTierSelection = () => {
  console.log('=== BACK TO TIER SELECTION ===')
  showMFRecommender.value = false
  tier3Step.value = 1
  // DON'T clear tier3Answers here in case user wants to edit
  // tier3Answers.value = {}
  tier3Recommendations.value = null
}

// Tier 3 workflow handlers
const handleQuestionnaireComplete = (answers) => {
  console.log('=== QUESTIONNAIRE COMPLETE ===', answers)
  tier3Answers.value = answers
  tier3Step.value = 2
}

const handleReviewEdit = () => {
  tier3Step.value = 1
}

const handleReviewSubmit = async () => {
  console.log('=== FETCHING RECOMMENDATIONS ===')
  isLoading.value = true

  try {
    // Call API to get recommendations
    const response = await store.getMutualFundRecommender({
      ...tier3Answers.value,
      goals: [{
        name: tier3Answers.value.goal1Type || 'Wealth growth',
        horizon: tier3Answers.value.goal1Horizon || '5+y',
        amount: selectedSIPAmount.value
      }]
    })

    console.log('API Response:', response)
    tier3Recommendations.value = response.mutualFundAdvice || response
    tier3Step.value = 3
  } catch (error) {
    console.error('Failed to fetch recommendations:', error)
    store.fireAlert('Error', 'Failed to generate recommendations. Please try again.')
  } finally {
    isLoading.value = false
  }
}

const handleSaveRecommendation = async () => {
  console.log('=== SAVING TIER 3 RECOMMENDATIONS ===')
  console.log('tier3Recommendations:', JSON.stringify(tier3Recommendations.value, null, 2))
  console.log('tier3Answers:', JSON.stringify(tier3Answers.value, null, 2))

  // CRITICAL: Verify tier3Answers has data
  if (!tier3Answers.value || Object.keys(tier3Answers.value).length === 0) {
    console.error('ERROR: tier3Answers is empty!')
    store.fireAlert('Error', 'Questionnaire data is missing. Please try again.')
    return
  }

  const anchors = tier3Recommendations.value?.anchors || []
  const satellites = tier3Recommendations.value?.satellites || []

  console.log('Anchors before processing:', JSON.stringify(anchors, null, 2))
  console.log('Satellites before processing:', JSON.stringify(satellites, null, 2))

  // Log individual fund allocations
  anchors.forEach((fund, idx) => {
    console.log(`Anchor ${idx + 1}:`, fund.fundName, 'allocationPercent:', fund.allocationPercent)
  })
  satellites.forEach((fund, idx) => {
    console.log(`Satellite ${idx + 1}:`, fund.fundName, 'allocationPercent:', fund.allocationPercent)
  })

  console.log('tier3Answers (questionnaire data):', JSON.stringify(tier3Answers.value, null, 2))

  const allocationData = {
    anchors,
    satellites,
    questionnaireAnswers: tier3Answers.value
  }

  console.log('Prepared allocation data for handleAllocationComplete:', JSON.stringify(allocationData, null, 2))
  await handleAllocationComplete(allocationData)
}

const getTierLabel = (tier) => {
  const tierNum = parseInt(tier)
  const tierLabels = {
    1: 'Starter',
    2: 'Growth',
    3: 'Advanced'
  }
  return tierLabels[tierNum] || 'Unknown'
}

const goToDashboard = () => {
  store.setTabView('Dashboard')
}

const formatNumber = (num) => {
  return new Intl.NumberFormat('en-IN').format(num)
}

const formatQuestionKey = (key) => {
  // Convert camelCase or snake_case to readable format
  return key
    .replace(/([A-Z])/g, ' $1')
    .replace(/_/g, ' ')
    .replace(/^./, (str) => str.toUpperCase())
    .trim()
}

const formatAnswerValue = (value) => {
  // Handle boolean values
  if (typeof value === 'boolean') {
    return value ? 'Yes' : 'No'
  }

  // Handle numeric 0 and 1 as Yes/No
  if (value === 0 || value === '0') {
    return 'No'
  }
  if (value === 1 || value === '1') {
    return 'Yes'
  }

  // Handle null/undefined
  if (value === null || value === undefined) {
    return 'Not specified'
  }

  // Format time horizons
  const stringValue = String(value)
  if (stringValue.includes('+y')) {
    return stringValue.replace('+y', '+ Years')
  }

  return stringValue
}

// Lifecycle
onMounted(async () => {
  // Show initial loading state
  isInitialLoading.value = true

  // Load existing SIP allocation if available
  try {
    const existingAllocation = await store.loadSIPAllocation(store.dashboardData?.customerId)

    if (existingAllocation && existingAllocation.allocations && existingAllocation.allocations.length > 0) {
      // Transform loaded data to display format
      const totalMonthlySIP = existingAllocation.allocations.reduce((sum, fund) => sum + (fund.monthlySIP || 0), 0)

      sipAllocationData.value = {
        monthlyAmount: totalMonthlySIP,
        funds: existingAllocation.allocations.map(fund => ({
          fundName: fund.fundName,
          category: fund.assetClass,
          sipAmount: fund.monthlySIP,
          percentage: fund.allocationPercentage,
          risk: fund.risk || 'Medium', // Default if not provided
          expectedReturn: fund.expectedReturn || null
        })),
        questionnaireAnswers: existingAllocation.questionnaireAnswers,
        sipTier: existingAllocation.sipTier,
        tierRange: existingAllocation.tierRange
      }

      selectedRange.value = parseInt(existingAllocation.sipTier)
      console.log('Existing SIP allocation loaded:', sipAllocationData.value)
    } else {
      console.log('No existing SIP allocation found - showing tier selection')
    }
  } catch (error) {
    console.error('Failed to load existing SIP allocation:', error)
  } finally {
    // Hide initial loading state after data is loaded (or failed)
    setTimeout(() => {
      isInitialLoading.value = false
    }, 800) // Small delay for smooth transition
  }
})
</script>

<style scoped>
.sip-portfolio-tab {
  min-height: 60vh;
}

.range-selection-container {
  max-width: 1200px;
  margin: 0 auto;
}

.intro-section {
  background: white;
  border-radius: 8px;
  padding: 16px 20px;
  margin-bottom: 16px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  text-align: center;
}

.intro-section h2 {
  color: var(--an-primary);
  margin-bottom: 8px;
  font-size: 1.5rem;
}

.intro-text {
  font-size: 0.9rem;
  line-height: 1.4;
  color: var(--an-text-secondary);
  margin: 0;
}

.range-cards-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
  margin-bottom: 20px;
}

.range-card {
  background: white;
  border-radius: 10px;
  padding: 16px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  cursor: pointer;
  transition: all 0.3s ease;
  border: 2px solid transparent;
  position: relative;
}

.range-card:hover {
  transform: translateY(-3px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
}

.range-card.selected {
  border-color: var(--an-primary);
  background: linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%);
}

.range-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
}

.range-icon {
  font-size: 1.5rem;
  color: var(--an-primary);
}

.range-header h3 {
  font-size: 1.15rem;
  color: var(--an-text-primary);
  margin: 0;
  font-weight: 600;
}

.range-amount {
  font-size: 1.4rem;
  font-weight: 700;
  color: var(--an-primary);
  margin-bottom: 8px;
  line-height: 1.2;
}

.range-description {
  font-size: 0.85rem;
  color: var(--an-text-secondary);
  line-height: 1.3;
}

.range-badge {
  position: absolute;
  top: 8px;
  right: 8px;
  background: linear-gradient(135deg, #f59e0b, #d97706);
  color: white;
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 0.65rem;
  font-weight: 700;
  display: flex;
  align-items: center;
  gap: 3px;
}

.action-section {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;
  margin-top: 8px;
}

.sip-amount-input-section {
  background: white;
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
  width: 100%;
  max-width: 500px;
}

.input-label {
  display: block;
  font-size: 1.1rem;
  font-weight: 600;
  color: var(--an-text-primary);
  margin-bottom: 12px;
  text-align: center;
}

.input-group {
  display: flex;
  align-items: center;
  gap: 8px;
  background: #f9fafb;
  border: 2px solid #e5e7eb;
  border-radius: 8px;
  padding: 4px 12px;
  transition: all 0.3s ease;
}

.input-group:focus-within {
  border-color: var(--an-primary);
  background: white;
  box-shadow: 0 0 0 3px rgba(13, 124, 104, 0.1);
}

.currency-symbol {
  font-size: 1.2rem;
  font-weight: 700;
  color: var(--an-primary);
}

.sip-amount-input {
  flex: 1;
  border: none !important;
  background: transparent !important;
  font-size: 1.2rem;
  font-weight: 600;
}

.sip-amount-input:focus {
  box-shadow: none !important;
  outline: none !important;
}

.input-hint {
  font-size: 0.85rem;
  color: var(--an-text-secondary);
  margin: 8px 0 0 0;
  text-align: center;
  font-style: italic;
}

.allocate-btn {
  min-width: 200px;
}

.mf-recommender-container {
  width: 100%;
}

.workflow-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  padding: 16px;
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.workflow-steps {
  display: flex;
  gap: 12px;
}

.workflow-step {
  padding: 6px 14px;
  border-radius: 999px;
  background: #f1f3f5;
  font-size: 0.85rem;
  font-weight: 600;
  color: #868e96;
  transition: all 0.2s;
}

.workflow-step.active {
  background: var(--an-primary);
  color: white;
}

.back-link-btn {
  border: none;
  background: transparent;
  color: var(--an-primary);
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  transition: color 0.2s;
}

.back-link-btn:hover {
  color: #0a6454;
}

.recommender-actions {
  display: flex;
  gap: 20px;
  justify-content: center;
  padding: 24px 0;
  margin-top: 20px;
  border-top: 2px solid #e5e7eb;
}

.fund-cards-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 14px;
  margin-bottom: 20px;
}

.fund-card {
  background: white;
  border-radius: 8px;
  padding: 14px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  border-left: 3px solid var(--an-primary);
}

.fund-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 8px;
  margin-bottom: 10px;
}

.fund-header h3 {
  font-size: 0.95rem;
  color: var(--an-text-primary);
  margin: 0;
  flex: 1;
  line-height: 1.3;
}

.fund-category-badge {
  background: #e0f2fe;
  color: #0369a1;
  padding: 3px 8px;
  border-radius: 10px;
  font-size: 0.7rem;
  font-weight: 600;
  white-space: nowrap;
}

.fund-allocation-amount {
  font-size: 1.25rem;
  font-weight: 700;
  color: var(--an-primary);
  margin-bottom: 6px;
  line-height: 1.2;
}

.fund-percentage {
  font-size: 0.8rem;
  color: var(--an-text-secondary);
  margin-bottom: 12px;
}

.fund-details {
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding-top: 12px;
  border-top: 1px solid #e5e7eb;
}

.fund-detail-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.detail-label {
  font-size: 0.8rem;
  color: var(--an-text-secondary);
  font-weight: 500;
}

.detail-value {
  font-size: 0.85rem;
  color: var(--an-text-primary);
  font-weight: 600;
}

.risk-low {
  color: #10b981;
}

.risk-medium {
  color: #f59e0b;
}

.risk-high {
  color: #ef4444;
}

.allocation-header {
  background: white;
  border-radius: 8px;
  padding: 12px 16px;
  margin-bottom: 14px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  text-align: center;
}

.allocation-header h2 {
  color: var(--an-primary);
  margin-bottom: 4px;
  font-size: 1.3rem;
  font-weight: 600;
}

.allocation-subtitle {
  font-size: 0.85rem;
  color: var(--an-text-secondary);
  margin: 0 0 6px 0;
  font-weight: 500;
}

.tier-info {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin-top: 4px;
}

.tier-badge {
  background: linear-gradient(135deg, var(--an-primary), #0a6454);
  color: white;
  padding: 3px 10px;
  border-radius: 12px;
  font-size: 0.7rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}

.tier-range {
  font-size: 0.75rem;
  color: var(--an-text-secondary);
  font-weight: 600;
}

/* Questionnaire Section */
.questionnaire-section {
  margin-top: 20px;
  width: 100%;
}

.questionnaire-card {
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  overflow: hidden;
}

/* Accordion Header Styling - Matching FinanceSnapshot */
.questionnaire-card :deep(.p-accordion-header) {
  background: linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%);
  border-bottom: 1px solid #bfdbfe;
}

.questionnaire-card :deep(.p-accordion-header-link) {
  padding: 12px 16px;
  border: none;
  background: transparent;
  transition: background-color 0.2s ease;
}

.questionnaire-card :deep(.p-accordion-header-link:hover) {
  background: linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%);
}

.accordion-header-content {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.9rem;
  font-weight: 700;
  color: #1e40af;
}

.accordion-header-content i {
  font-size: 1rem;
  color: #3b82f6;
}

/* Accordion Content */
.questionnaire-card :deep(.p-accordion-content) {
  padding: 0;
  border: none;
}

.preferences-content {
  padding: 20px;
  background: linear-gradient(135deg, #fafbfc 0%, #f0f4f8 100%);
}

.preferences-intro {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.9rem;
  color: #475569;
  margin: 0 0 20px 0;
  padding: 12px 16px;
  background: white;
  border-radius: 8px;
  border-left: 4px solid #3b82f6;
  font-weight: 500;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}

.preferences-intro i {
  color: #3b82f6;
  font-size: 1rem;
  flex-shrink: 0;
}

/* Preferences Grid - Matching FinanceSnapshot calc-grid */
.preferences-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 14px;
}

@media (max-width: 768px) {
  .preferences-grid {
    grid-template-columns: 1fr;
  }
}

.preference-card {
  padding: 16px 18px;
  background: white;
  border-radius: 8px;
  border-left: 4px solid #3b82f6;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.06);
  transition: all 0.2s ease;
  position: relative;
  overflow: hidden;
}

.preference-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 2px;
  background: linear-gradient(90deg, #3b82f6, #60a5fa);
  opacity: 0;
  transition: opacity 0.2s ease;
}

.preference-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.15);
}

.preference-card:hover::before {
  opacity: 1;
}

.preference-label {
  font-size: 0.75rem;
  font-weight: 600;
  color: #64748b;
  margin-bottom: 8px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.preference-value {
  font-size: 1.05rem;
  color: #1e293b;
  font-weight: 700;
}

.action-buttons {
  display: flex;
  gap: 20px;
  justify-content: center;
  padding: 32px 0;
}

.action-btn {
  min-width: 180px;
}

.loading-state {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 400px;
}

.loading-content {
  text-align: center;
  background: white;
  padding: 32px;
  border-radius: 12px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
}

.loading-icon {
  font-size: 3rem;
  color: var(--an-primary);
  margin-bottom: 16px;
}

.loading-content h3 {
  font-size: 1.3rem;
  color: var(--an-text-primary);
  margin: 0 0 8px 0;
}

.loading-content p {
  font-size: 1rem;
  color: var(--an-text-secondary);
  margin: 0;
}

/* Initial Loading State */
.initial-loading-state {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 70vh;
  animation: fadeIn 0.3s ease;
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

.initial-loading-content {
  text-align: center;
  background: white;
  padding: 48px 40px;
  border-radius: 16px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
  max-width: 500px;
}

.loading-spinner-container {
  margin-bottom: 24px;
}

.loading-spinner {
  font-size: 4rem;
  color: var(--an-primary);
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.initial-loading-content h3 {
  font-size: 1.5rem;
  color: var(--an-text-primary);
  margin: 0 0 12px 0;
  font-weight: 700;
}

.initial-loading-content p {
  font-size: 1rem;
  color: var(--an-text-secondary);
  margin: 0 0 24px 0;
  line-height: 1.5;
}

.loading-dots {
  display: flex;
  justify-content: center;
  gap: 8px;
}

.loading-dots span {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: var(--an-primary);
  animation: bounce 1.4s infinite ease-in-out both;
}

.loading-dots span:nth-child(1) {
  animation-delay: -0.32s;
}

.loading-dots span:nth-child(2) {
  animation-delay: -0.16s;
}

@keyframes bounce {
  0%, 80%, 100% {
    transform: scale(0);
    opacity: 0.5;
  }
  40% {
    transform: scale(1);
    opacity: 1;
  }
}

/* Responsive */
@media (max-width: 1024px) {
  .range-cards-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 768px) {
  .range-cards-grid {
    grid-template-columns: 1fr;
  }

  .fund-cards-grid {
    grid-template-columns: 1fr;
  }

  .action-buttons {
    flex-direction: column;
    align-items: center;
  }

  .action-btn {
    width: 100%;
    max-width: 300px;
  }
}
</style>
